/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {IconButton} from '../../../src/components/buttons/IconButton';
import {
  MapLocateIcon
} from '../../../src/icons/MapLocateIcon';
import {
  MapPinIcon
} from '../../../src/icons/MapPinIcon';
import {
  SystemFindNearestIcon
} from '../../../src/icons/SystemFindNearestIcon';
import {
  SystemMaintenanceScheduleIcon
} from '../../../src/icons/SystemMaintenanceScheduleIcon';
import {
  SystemViewHistoryIcon
} from '../../../src/icons/SystemViewHistoryIcon';
import {dlsColor} from '../../../src/theme/theme';
import {useCallbackWithValue} from '../../hooks/useCallbackWithValue';
import {navigateToGoogleDirections, navigateToGoogleStreetView} from '../../maps/helpers/globeUtils';
import useIsHistoryAvailable from '../../maps/hooks/useIsHistoryAvailable';
import Tooltip from '../tooltip/Tooltip';

export interface PanelCommentsProps {
  findNearbyClick?: Function;
  viewHistoryClick?: Function | null;
  viewMaintenanceScheduleClick?: Function | null;
  streetViewClick?: Function | null;
  labels: {
    viewHistory: string;
    findNearby: string;
    maintenanceSchedule: string;
    navigateTo: string;
    streetView: string;
  },
  disabled?: boolean;
  iconDirection?: string;
  assetData: CVSAssetType
}

const useStyles = makeStyles(theme => ({
  root : {
    maxWidth                       : '100%',
    display                        : 'flex',
    alignItems                     : 'center',
    minHeight                      : 48,
    padding                        : '0 8px',
    [theme.breakpoints.down('sm')] : {
      justifyContent : 'space-evenly'
    }
  },
  icon : {
    marginRight : 4
  }
}));

/**
* @PanelComments
*
* Defines Vehicle Comments component. This component is reusable and can be custom
* rendered with props.
*
* ```typescript
* import React from 'react';
* import PanelComments from '@ion/dls/src/components/sections/PanelComments';
*
* <PanelComments labels={{(...labels object)}} findHistory={findHistory} viewNearby={viewNearby} />
 *
 * ```
*/
const PanelComments = ({
  findNearbyClick,
  viewHistoryClick,
  viewMaintenanceScheduleClick,
  streetViewClick,
  disabled,
  labels,
  assetData
}: PanelCommentsProps) => {
  const classes          = useStyles();
  const isHistoryEnabled = useIsHistoryAvailable(assetData);

  /*
   * Defers to the consumer's handler when one is supplied so the panel can open
   * an in-app Street View dialog, and falls back to navigating out to Google
   * Maps for consumers that have no dialog mounted.
   */
  const showStreetView = useCallback(function(e) {
    e.stopPropagation();
    e.preventDefault();
    if (streetViewClick) {
      streetViewClick(assetData);
    }
    if (!streetViewClick) {
      navigateToGoogleStreetView(assetData);
    }
  }, [assetData, streetViewClick]);

  const navigateToAsset = useCallback(function(e) {
    e.stopPropagation();
    e.preventDefault();
    navigateToGoogleDirections(assetData);
  }, [assetData]);

  const onViewHistory = useCallbackWithValue(viewHistoryClick, assetData, true);

  return (
    <div className={classes.root}>
      {viewHistoryClick && (
        <span className={classes.icon}>
          <Tooltip title={labels[isHistoryEnabled ? 'viewHistory' : 'noHistory']} arrow>
            <span>
              <IconButton icon={<SystemViewHistoryIcon />}
                color={isHistoryEnabled ? dlsColor('caBlue') : dlsColor('grey4')}
                padding={'8px'}
                onClick={onViewHistory}
                disabled={!isHistoryEnabled} />
            </span>
          </Tooltip>
        </span>
      )}
      <span className={classes.icon}>
        <Tooltip title={labels.streetView} arrow>
          <span>
            <IconButton icon={<MapPinIcon />}
              color={dlsColor('caBlue')}
              padding={'8px'}
              onClick={showStreetView} />
          </span>
        </Tooltip>
      </span>
      <span className={classes.icon}>
        <Tooltip title={labels.navigateTo} arrow>
          <span>
            <IconButton icon={<MapLocateIcon />}
              color={dlsColor('caBlue')}
              padding={'8px'}
              onClick={navigateToAsset} />
          </span>
        </Tooltip>
      </span>
      {findNearbyClick && (
        <span className={classes.icon}>
          <Tooltip title={labels.findNearby} arrow>
            <span>
              <IconButton icon={<SystemFindNearestIcon />}
                color={dlsColor('caBlue')}
                padding={'8px'}
                onClick={findNearbyClick} />
            </span>
          </Tooltip>
        </span>
      )}
      {viewMaintenanceScheduleClick && (
        <span className={classes.icon}>
          <Tooltip title={labels.maintenanceSchedule} arrow>
            <span>
              <IconButton icon={<SystemMaintenanceScheduleIcon />}
                color={!disabled ? dlsColor('caBlue') : dlsColor('grey4')}
                padding={'8px'}
                disabled={disabled}
                onClick={viewMaintenanceScheduleClick} />
            </span>
          </Tooltip>
        </span>
      )}
    </div>
  );
};

export default PanelComments;
